import * as Sentry from "@sentry/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createContext, useContext, useState } from "react";
import {
  getOrCreateNutritionResearchThread,
  sendNutritionResearchMessage,
  runNutritionResearchThread,
  streamNutritionResearchThread,
  getNutritionResearchThreadMessages,
} from "../../fay/Api";
import {
  NutritionResearchMessageDetails,
  NutritionResearchRunThreadResponse,
} from "../../fay/generated";

interface ResearchChatContextType {
  fetchPreviousThreadMessages: (threadId: string) => Promise<void>;
  fetchLatestThreadMessage: (threadId: string) => Promise<void>;
  isFetchingThreadMessages: boolean;
  hasMoreMessages: boolean;
  threadId: string | undefined;
  threadMessages: NutritionResearchMessageDetails[] | undefined;
  sendNutritionResearchMessage: (
    threadId: string,
    message: string,
  ) => Promise<void>;
  streamNutritionResearchThread: (
    threadId: string,
  ) => Promise<ReadableStream<Uint8Array>>;
  runNutritionResearchThread: (
    threadId: string,
  ) => Promise<NutritionResearchRunThreadResponse>;
  isSendingMessage: boolean;
  isRunningThread: boolean;
  getMessageError: Error | null;
  sendMessageError: Error | null;
  runThreadError: Error | null;
}

const ResearchChatContext = createContext<ResearchChatContextType | undefined>(
  undefined,
);

const dedupeThreadMessages = (
  currentMessages: NutritionResearchMessageDetails[],
  newMessages: NutritionResearchMessageDetails[],
) => {
  const threadMessageIdSet = new Set(
    currentMessages.map((threadMessage) => threadMessage.message_id),
  );
  return newMessages.filter(
    (threadMessage) => !threadMessageIdSet.has(threadMessage.message_id),
  );
};

export const FETCH_THREAD_MESSAGES_PAGE_SIZE = 10;

export const ResearchChatProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [threadMessages, setThreadMessages] = useState<
    NutritionResearchMessageDetails[]
  >([]);
  const [isFetchingThreadMessages, setIsFetchingThreadMessages] =
    useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [getMessageError, setGetMessageError] = useState<Error | null>(null);

  const threadMessageCompareFnAsc = (
    a: NutritionResearchMessageDetails,
    b: NutritionResearchMessageDetails,
  ) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime();

  const fetchLatestThreadMessage = async (threadId: string) => {
    setIsFetchingThreadMessages(true);
    const response = await getNutritionResearchThreadMessages(threadId, {
      page_size: 3,
      offset: 0,
    });

    const newThreadMessages = dedupeThreadMessages(
      threadMessages,
      response.messages,
    );

    setThreadMessages(
      [...threadMessages, ...newThreadMessages].toSorted(
        threadMessageCompareFnAsc,
      ),
    );

    setIsFetchingThreadMessages(false);
  };

  const fetchPreviousThreadMessages = async (threadId: string) => {
    setIsFetchingThreadMessages(true);

    try {
      const response = await getNutritionResearchThreadMessages(threadId, {
        page_size: FETCH_THREAD_MESSAGES_PAGE_SIZE,
        offset: currentOffset,
      });

      if (response.messages.length === 0) {
        setHasMoreMessages(false);
      } else {
        const newThreadMessages = dedupeThreadMessages(
          threadMessages,
          response.messages,
        );

        setThreadMessages(
          [...threadMessages, ...newThreadMessages].toSorted(
            threadMessageCompareFnAsc,
          ),
        );

        setCurrentOffset(currentOffset + FETCH_THREAD_MESSAGES_PAGE_SIZE);
      }
    } catch (e) {
      Sentry.captureException(e);
      setGetMessageError(new Error("Failed to load previous thread messages"));
    }

    setIsFetchingThreadMessages(false);
  };

  const { data: getInitialThreadMessagesResponse } = useQuery({
    queryKey: [
      "getInitialThreadMessages",
      { page_size: FETCH_THREAD_MESSAGES_PAGE_SIZE, offset: 0 },
    ],
    queryFn: async () => {
      setIsFetchingThreadMessages(true);

      let response;
      try {
        response = await getOrCreateNutritionResearchThread("me", {
          page_size: FETCH_THREAD_MESSAGES_PAGE_SIZE,
          offset: 0,
        });

        setThreadMessages(
          response.messages.toSorted(threadMessageCompareFnAsc),
        );

        setCurrentOffset(FETCH_THREAD_MESSAGES_PAGE_SIZE);
      } catch (e) {
        Sentry.captureException(e);
        setGetMessageError(new Error("Failed to load initial thread messages"));
      }

      setIsFetchingThreadMessages(false);
      return response;
    },
    staleTime: Infinity,
  });

  const {
    mutateAsync: _sendNutritionResearchMessage,
    error: sendMessageError,
    isPending: isSendingMessage,
  } = useMutation({
    mutationFn: async ({
      threadId,
      message,
    }: {
      threadId: string;
      message: string;
    }) => {
      try {
        return await sendNutritionResearchMessage(threadId, message);
      } catch (e) {
        Sentry.captureException(e);
        throw new Error("Failed to send message. Please try Again");
      }
    },
  });

  const {
    mutateAsync: _runNutritionResearchThread, // eslint-disable-line @typescript-eslint/no-unused-vars
    error: runThreadError,
    isPending: isRunningThread,
  } = useMutation({
    mutationFn: async (threadId: string) => {
      try {
        return await runNutritionResearchThread(threadId);
      } catch (e) {
        Sentry.captureException(e);
        throw new Error("There was an error generating a response");
      }
    },
  });

  const value = {
    fetchPreviousThreadMessages,
    fetchLatestThreadMessage,
    isFetchingThreadMessages,
    hasMoreMessages,
    threadId: getInitialThreadMessagesResponse?.threadId,
    threadMessages: threadMessages,
    sendNutritionResearchMessage: async (threadId: string, message: string) =>
      await _sendNutritionResearchMessage({ threadId, message }),
    streamNutritionResearchThread,
    runNutritionResearchThread,
    getMessageError,
    sendMessageError,
    runThreadError,
    isRunningThread,
    isSendingMessage,
  };

  return (
    <ResearchChatContext.Provider value={value}>
      {children}
    </ResearchChatContext.Provider>
  );
};

export const useResearchChat = () => {
  const context = useContext(ResearchChatContext);
  if (context === undefined) {
    throw new Error(
      "useResearchChat must be used within a ResearchChatProvider",
    );
  }
  return context;
};
